import React, { useState, useEffect, useReducer } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

const CompanionForge: React.FC = () => {
  const {
    companions,
    formatNumber,
    startMining,
    removeFromMining,
    updateMining,
    updateContinuousMining,
    updateArmorCrafting,
    updateWeaponCrafting,
    getMiningProgress,
    isCompanionMining,
    craftArmor,
    craftWeapon,
    getArmorCraftingProgress,
    getWeaponCraftingProgress,
    resetArmorLevel,
    resetWeaponLevel,

    getOreCounts,
    getCraftableArmor,
    getCraftableWeapons,
  } = useGameStore();

  const [selectedTab, setSelectedTab] = useState<'mining' | 'crafting' | 'weapons'>('mining');
  const [showMiningCompanions, setShowMiningCompanions] = useState(false);
  const [showActiveCrafting, setShowActiveCrafting] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<'iron' | 'steel' | 'mithril' | 'adamantium'>('iron');
  const [selectedArmorCategory, setSelectedArmorCategory] = useState<'all' | 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield'>('all');
  const [selectedWeaponCategory, setSelectedWeaponCategory] = useState<'all' | 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff'>('all');
  const [expandedArmorGroup, setExpandedArmorGroup] = useState<string | null>(null);
  const [expandedWeaponGroup, setExpandedWeaponGroup] = useState<string | null>(null);
  
  // Verfügbare Begleiter für Mining
  const availableCompanions = companions.discovered.filter(companion => !isCompanionMining(companion.id)).length;

  // Mining-Updates werden jetzt zentral in der App.tsx verwaltet
  // um doppelte Ausführung zu vermeiden

  // Force re-render when companions change to update ore production display
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate();
    }, 1000); // Update UI every second to reflect level changes

    return () => clearInterval(interval);
  }, []);

  // Ore counts werden automatisch aktualisiert - direkt aus dem Store
  const oreCounts = companions.forge?.ore || { iron: 0, steel: 0, mithril: 0, adamantium: 0 };
  const craftableArmor = getCraftableArmor();

  // Prüfe ob ein Erztyp verfügbar ist basierend auf progressiven Anforderungen
  const isOreTypeAvailable = (oreType: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
    const oreRequirements = {
      iron: 0,
      steel: 10000,
      mithril: 50000,
      adamantium: 100000,
    };

    switch (oreType) {
      case 'iron':
        return true; // Eisen ist immer verfügbar
      case 'steel':
        return oreCounts.iron >= oreRequirements.steel;
      case 'mithril':
        return oreCounts.steel >= oreRequirements.mithril;
      case 'adamantium':
        return oreCounts.mithril >= oreRequirements.adamantium;
      default:
        return false;
    }
  };

  const getOreRequirement = (oreType: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
    const oreRequirements = {
      iron: 0,
      steel: 10000,
      mithril: 50000,
      adamantium: 100000,
    };

    switch (oreType) {
      case 'iron':
        return null;
      case 'steel':
        return { type: 'iron', amount: oreRequirements.steel };
      case 'mithril':
        return { type: 'steel', amount: oreRequirements.mithril };
      case 'adamantium':
        return { type: 'mithril', amount: oreRequirements.adamantium };
      default:
        return null;
    }
  };

  const calculateOrePerSecond = (companionId: string, oreType?: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
    const companion = companions.discovered.find(c => c.id === companionId);
    if (!companion) return 0;

    const efficiency = companion.level || 1;
    const baseOreGain = 10; // Basis-Erz pro Minute für Eisen
    
    // Bestimme den Erz-Typ für die Berechnung
    let currentOreType: 'iron' | 'steel' | 'mithril' | 'adamantium';
    
    if (!isCompanionMining(companionId)) {
      // Wenn der Begleiter nicht im Mining ist, verwende den angegebenen Erz-Typ
      if (!oreType) return 0;
      currentOreType = oreType;
    } else {
      // Wenn der Begleiter im Mining ist, hole den aktuellen Erz-Typ
      const mining = companions.forge?.mining?.activeMiners[companionId];
      if (!mining) return 0;
      currentOreType = mining.oreType;
    }
    
    // Berechne die Produktionsrate basierend auf dem Erz-Typ
    let orePerMinute: number;
    switch (currentOreType) {
      case 'iron':
        orePerMinute = baseOreGain * efficiency; // 10 * Level pro Minute
        break;
      case 'steel':
        orePerMinute = (baseOreGain * efficiency) / 10; // 10x langsamer
        break;
      case 'mithril':
        orePerMinute = (baseOreGain * efficiency) / 100; // 100x langsamer
        break;
      case 'adamantium':
        orePerMinute = (baseOreGain * efficiency) / 1000; // 1000x langsamer
        break;
      default:
        orePerMinute = baseOreGain * efficiency;
    }
    
    const orePerSecond = orePerMinute / 60; // Konvertiere zu pro Sekunde
    return orePerSecond;
  };

  // Funktion zur Berechnung der XP pro Sekunde für jeden Begleiter
  const calculateXpPerSecond = (companionId: string, oreType?: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
    const companion = companions.discovered.find(c => c.id === companionId);
    if (!companion) return 0;

    const efficiency = companion.level || 1;
    const baseXpPerSecond = 0.1; // Basis-XP pro Sekunde
    
    // Bestimme den Erz-Typ für die Berechnung
    let currentOreType: 'iron' | 'steel' | 'mithril' | 'adamantium';
    
    if (!isCompanionMining(companionId)) {
      // Wenn der Begleiter nicht im Mining ist, verwende den angegebenen Erz-Typ
      if (!oreType) return 0;
      currentOreType = oreType;
    } else {
      // Wenn der Begleiter im Mining ist, hole den aktuellen Erz-Typ
      const mining = companions.forge?.mining?.activeMiners[companionId];
      if (!mining) return 0;
      currentOreType = mining.oreType;
    }
    
    // Berechne die XP-Rate basierend auf dem Erz-Typ (wie im GameStore)
    let xpGain: number;
    switch (currentOreType) {
      case 'iron':
        xpGain = baseXpPerSecond * efficiency; // Standard XP
        break;
      case 'steel':
        xpGain = baseXpPerSecond * efficiency * 10; // 10x mehr XP
        break;
      case 'mithril':
        xpGain = baseXpPerSecond * efficiency * 100; // 100x mehr XP
        break;
      case 'adamantium':
        xpGain = baseXpPerSecond * efficiency * 1000; // 1000x mehr XP
        break;
      default:
        xpGain = baseXpPerSecond * efficiency;
    }
    
    return xpGain;
  };

  const getOreIcon = (oreType: string) => {
    switch (oreType) {
      case 'iron': return '🪨';
      case 'steel': return '⚒️';
      case 'mithril': return '💎';
      case 'adamantium': return '🔮';
      default: return '🪨';
    }
  };

  const getOreColor = (oreType: string) => {
    switch (oreType) {
      case 'iron': return 'from-gray-500 to-gray-600';
      case 'steel': return 'from-blue-500 to-blue-600';
      case 'mithril': return 'from-purple-500 to-purple-600';
      case 'adamantium': return 'from-red-500 to-red-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getArmorIcon = (armorType: string) => {
    switch (armorType) {
      case 'helmet': return '⛑️';
      case 'chestplate': return '🛡️';
      case 'greaves': return '🦵';
      case 'boots': return '👢';
      case 'shield': return '🛡️';
      default: return '⚔️';
    }
  };

  const getWeaponIcon = (weaponType: string) => {
    switch (weaponType) {
      case 'sword': return '⚔️';
      case 'axe': return '🪓';
      case 'mace': return '🔨';
      case 'dagger': return '🗡️';
      case 'bow': return '🏹';
      case 'staff': return '🦯';
      default: return '⚔️';
    }
  };

  const getMaterialColor = (material: string) => {
    switch (material) {
      case 'iron': return 'from-gray-400 to-gray-500';
      case 'steel': return 'from-blue-400 to-blue-500';
      case 'mithril': return 'from-purple-400 to-purple-500';
      case 'adamantium': return 'from-red-400 to-red-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const startMiningForCompanion = (companionId: string, oreType: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
    const duration = 30; // Standard-Dauer von 30 Minuten
    const success = startMining(companionId, oreType, duration);
    
    if (!success) {
      // Zeige Fehlermeldung basierend auf Erztyp
      const oreRequirements = {
        steel: 10000,
        mithril: 50000,
        adamantium: 100000,
      };
      
      let message = '';
      switch (oreType) {
        case 'steel':
          message = `Du brauchst mindestens ${oreRequirements.steel} Eisen, um Stahl abzubauen!`;
          break;
        case 'mithril':
          message = `Du brauchst mindestens ${oreRequirements.mithril} Stahl, um Mithril abzubauen!`;
          break;
        case 'adamantium':
          message = `Du brauchst mindestens ${oreRequirements.adamantium} Mithril, um Adamantium abzubauen!`;
          break;
      }
      
      // Hier könnte man eine Toast-Nachricht oder Alert anzeigen
      alert(message);
    }
  };

  const getArmorLevel = (armorType: string, material: string) => {
    const existingArmor = Object.values(companions.forge?.armor || {})
      .filter((armor: any) => armor.type === armorType && armor.material === material)
      .sort((a: any, b: any) => (b.level || 0) - (a.level || 0));
    
    return existingArmor.length > 0 ? (existingArmor[0].level || 0) + 1 : 1;
  };

  const getWeaponLevel = (weaponType: string, material: string) => {
    const existingWeapons = Object.values(companions.forge?.weapons || {})
      .filter((weapon: any) => weapon.type === weaponType && weapon.material === material)
      .sort((a: any, b: any) => (b.level || 0) - (a.level || 0));
    
    return existingWeapons.length > 0 ? (existingWeapons[0].level || 0) + 1 : 1;
  };

  const getAdjustedArmorCost = (armor: any) => {
    const nextLevel = getArmorLevel(armor.type, armor.material);
    const levelCostMultiplier = Math.pow(1.5, nextLevel - 1);
    
    return {
      iron: Math.ceil(armor.cost.iron * levelCostMultiplier),
      steel: Math.ceil(armor.cost.steel * levelCostMultiplier),
      mithril: Math.ceil(armor.cost.mithril * levelCostMultiplier),
      adamantium: Math.ceil(armor.cost.adamantium * levelCostMultiplier),
    };
  };

  const getAdjustedArmorStats = (armor: any) => {
    const nextLevel = getArmorLevel(armor.type, armor.material);
    
    return {
      defense: armor.defense * nextLevel,
      durability: armor.durability * nextLevel,
      maxDurability: armor.durability * nextLevel,
    };
  };

  const getAdjustedWeaponCost = (weapon: any) => {
    const nextLevel = getWeaponLevel(weapon.type, weapon.material);
    const levelCostMultiplier = Math.pow(1.5, nextLevel - 1);
    
    return {
      iron: Math.ceil(weapon.cost.iron * levelCostMultiplier),
      steel: Math.ceil(weapon.cost.steel * levelCostMultiplier),
      mithril: Math.ceil(weapon.cost.mithril * levelCostMultiplier),
      adamantium: Math.ceil(weapon.cost.adamantium * levelCostMultiplier),
    };
  };

  const getAdjustedWeaponStats = (weapon: any) => {
    const nextLevel = getWeaponLevel(weapon.type, weapon.material);
    
    return {
      attack: weapon.attack * nextLevel,
      durability: weapon.durability * nextLevel,
      maxDurability: weapon.durability * nextLevel,
    };
  };

  const canAffordArmor = (armor: any) => {
    const adjustedCost = getAdjustedArmorCost(armor);
    
    // Prüfe ob genug Erz vorhanden ist (nur für Erzsorten mit Kosten > 0)
    const hasEnoughOre = (adjustedCost.iron === 0 || oreCounts.iron >= adjustedCost.iron) &&
           (adjustedCost.steel === 0 || oreCounts.steel >= adjustedCost.steel) &&
           (adjustedCost.mithril === 0 || oreCounts.mithril >= adjustedCost.mithril) &&
           (adjustedCost.adamantium === 0 || oreCounts.adamantium >= adjustedCost.adamantium);
    
    // Prüfe ob das Material freigeschaltet ist
    const oreRequirements = {
      iron: 0,
      steel: 10000,
      mithril: 50000,
      adamantium: 100000,
    };
    
    let isMaterialUnlocked = true;
    switch (armor.material) {
      case 'steel':
        isMaterialUnlocked = oreCounts.iron >= oreRequirements.steel;
        break;
      case 'mithril':
        isMaterialUnlocked = oreCounts.steel >= oreRequirements.mithril;
        break;
      case 'adamantium':
        isMaterialUnlocked = oreCounts.mithril >= oreRequirements.adamantium;
        break;
    }
    
    return hasEnoughOre && isMaterialUnlocked;
  };

  const canAffordWeapon = (weapon: any) => {
    const adjustedCost = getAdjustedWeaponCost(weapon);
    
    // Prüfe ob genug Erz vorhanden ist (nur für Erzsorten mit Kosten > 0)
    const hasEnoughOre = (adjustedCost.iron === 0 || oreCounts.iron >= adjustedCost.iron) &&
           (adjustedCost.steel === 0 || oreCounts.steel >= adjustedCost.steel) &&
           (adjustedCost.mithril === 0 || oreCounts.mithril >= adjustedCost.mithril) &&
           (adjustedCost.adamantium === 0 || oreCounts.adamantium >= adjustedCost.adamantium);
    
    // Prüfe ob das Material freigeschaltet ist
    const oreRequirements = {
      iron: 0,
      steel: 10000,
      mithril: 50000,
      adamantium: 100000,
    };
    
    let isMaterialUnlocked = true;
    switch (weapon.material) {
      case 'steel':
        isMaterialUnlocked = oreCounts.iron >= oreRequirements.steel;
        break;
      case 'mithril':
        isMaterialUnlocked = oreCounts.steel >= oreRequirements.mithril;
        break;
      case 'adamantium':
        isMaterialUnlocked = oreCounts.mithril >= oreRequirements.adamantium;
        break;
    }
    
    return hasEnoughOre && isMaterialUnlocked;
  };

  const getArmorButtonText = (armor: any) => {
    const adjustedCost = getAdjustedArmorCost(armor);
    const nextLevel = getArmorLevel(armor.type, armor.material);
    
    // Prüfe ob genug Erz vorhanden ist (nur für Erzsorten mit Kosten > 0)
    const hasEnoughOre = (adjustedCost.iron === 0 || oreCounts.iron >= adjustedCost.iron) &&
           (adjustedCost.steel === 0 || oreCounts.steel >= adjustedCost.steel) &&
           (adjustedCost.mithril === 0 || oreCounts.mithril >= adjustedCost.mithril) &&
           (adjustedCost.adamantium === 0 || oreCounts.adamantium >= adjustedCost.adamantium);
    
    // Prüfe ob das Material freigeschaltet ist
    const oreRequirements = {
      iron: 0,
      steel: 10000,
      mithril: 50000,
      adamantium: 100000,
    };
    
    let isMaterialUnlocked = true;
    switch (armor.material) {
      case 'steel':
        isMaterialUnlocked = oreCounts.iron >= oreRequirements.steel;
        break;
      case 'mithril':
        isMaterialUnlocked = oreCounts.steel >= oreRequirements.mithril;
        break;
      case 'adamantium':
        isMaterialUnlocked = oreCounts.mithril >= oreRequirements.adamantium;
        break;
    }
    
    if (!isMaterialUnlocked) {
      switch (armor.material) {
        case 'steel':
          return `❌ Braucht ${formatNumber(oreRequirements.steel)} Eisen`;
        case 'mithril':
          return `❌ Braucht ${formatNumber(oreRequirements.mithril)} Stahl`;
        case 'adamantium':
          return `❌ Braucht ${formatNumber(oreRequirements.adamantium)} Mithril`;
        default:
          return '❌ Material gesperrt';
      }
    }
    
    if (!hasEnoughOre) {
      const missingOres = [];
      if (oreCounts.iron < adjustedCost.iron) missingOres.push(`${formatNumber(adjustedCost.iron)} Eisen`);
      if (oreCounts.steel < adjustedCost.steel) missingOres.push(`${formatNumber(adjustedCost.steel)} Stahl`);
      if (oreCounts.mithril < adjustedCost.mithril) missingOres.push(`${formatNumber(adjustedCost.mithril)} Mithril`);
      if (oreCounts.adamantium < adjustedCost.adamantium) missingOres.push(`${formatNumber(adjustedCost.adamantium)} Adamantium`);
      
      return `❌ Level ${nextLevel}: ${missingOres.join(', ')}`;
    }
    
    return `🔨 Level ${nextLevel} Schmieden`;
  };

  const getWeaponButtonText = (weapon: any) => {
    const adjustedCost = getAdjustedWeaponCost(weapon);
    const nextLevel = getWeaponLevel(weapon.type, weapon.material);
    
    // Prüfe ob genug Erz vorhanden ist (nur für Erzsorten mit Kosten > 0)
    const hasEnoughOre = (adjustedCost.iron === 0 || oreCounts.iron >= adjustedCost.iron) &&
           (adjustedCost.steel === 0 || oreCounts.steel >= adjustedCost.steel) &&
           (adjustedCost.mithril === 0 || oreCounts.mithril >= adjustedCost.mithril) &&
           (adjustedCost.adamantium === 0 || oreCounts.adamantium >= adjustedCost.adamantium);
    
    // Prüfe ob das Material freigeschaltet ist
    const oreRequirements = {
      iron: 0,
      steel: 10000,
      mithril: 50000,
      adamantium: 100000,
    };
    
    let isMaterialUnlocked = true;
    switch (weapon.material) {
      case 'steel':
        isMaterialUnlocked = oreCounts.iron >= oreRequirements.steel;
        break;
      case 'mithril':
        isMaterialUnlocked = oreCounts.steel >= oreRequirements.mithril;
        break;
      case 'adamantium':
        isMaterialUnlocked = oreCounts.mithril >= oreRequirements.adamantium;
        break;
    }
    
    if (!isMaterialUnlocked) {
      switch (weapon.material) {
        case 'steel':
          return `❌ Braucht ${formatNumber(oreRequirements.steel)} Eisen`;
        case 'mithril':
          return `❌ Braucht ${formatNumber(oreRequirements.mithril)} Stahl`;
        case 'adamantium':
          return `❌ Braucht ${formatNumber(oreRequirements.adamantium)} Mithril`;
        default:
          return '❌ Material gesperrt';
      }
    }
    
    if (!hasEnoughOre) {
      const missingOres = [];
      if (oreCounts.iron < adjustedCost.iron) missingOres.push(`${formatNumber(adjustedCost.iron)} Eisen`);
      if (oreCounts.steel < adjustedCost.steel) missingOres.push(`${formatNumber(adjustedCost.steel)} Stahl`);
      if (oreCounts.mithril < adjustedCost.mithril) missingOres.push(`${formatNumber(adjustedCost.mithril)} Mithril`);
      if (oreCounts.adamantium < adjustedCost.adamantium) missingOres.push(`${formatNumber(adjustedCost.adamantium)} Adamantium`);
      
      return `❌ Level ${nextLevel}: ${missingOres.join(', ')}`;
    }
    
    return `⚔️ Level ${nextLevel} Schmieden`;
  };

  const getActiveCrafting = () => {
    if (!companions.forge?.armor) {
      return [];
    }
    
    const activeArmor = Object.values(companions.forge.armor).filter(armor => armor.isCrafting);
    
    // Gruppiere nach Typ und Material
    const groupedArmor: { [key: string]: any[] } = {};
    activeArmor.forEach(armor => {
      const key = `${armor.type}_${armor.material}`;
      if (!groupedArmor[key]) {
        groupedArmor[key] = [];
      }
      groupedArmor[key].push(armor);
    });
    
    // Erstelle gestapelte Projekte
    return Object.entries(groupedArmor).map(([key, items]) => {
      const firstItem = items[0];
      const totalProgress = items.reduce((sum, item) => {
        const progress = getArmorCraftingProgress(item.id);
        return sum + progress;
      }, 0);
      const averageProgress = totalProgress / items.length;
      
      return {
        ...firstItem,
        count: items.length,
        averageProgress,
        items: items
      };
    });
  };

  const getActiveWeaponCrafting = () => {
    if (!companions.forge?.weapons) {
      return [];
    }
    
    const activeWeapons = Object.values(companions.forge.weapons).filter(weapon => weapon.isCrafting);
    
    // Gruppiere nach Typ und Material
    const groupedWeapons: { [key: string]: any[] } = {};
    activeWeapons.forEach(weapon => {
      const key = `${weapon.type}_${weapon.material}`;
      if (!groupedWeapons[key]) {
        groupedWeapons[key] = [];
      }
      groupedWeapons[key].push(weapon);
    });
    
    // Erstelle gestapelte Projekte
    return Object.entries(groupedWeapons).map(([key, items]) => {
      const firstItem = items[0];
      const totalProgress = items.reduce((sum, item) => {
        const progress = getWeaponCraftingProgress(item.id);
        return sum + progress;
      }, 0);
      const averageProgress = totalProgress / items.length;
      
      return {
        ...firstItem,
        count: items.length,
        averageProgress,
        items: items
      };
    });
  };

  const getFilteredArmor = () => {
    const materialFiltered = craftableArmor.filter(armor => armor.material === selectedMaterial);
    if (selectedArmorCategory === 'all') return materialFiltered;
    return materialFiltered.filter(armor => armor.type === selectedArmorCategory);
  };

  const getFilteredWeapons = () => {
    const craftableWeapons = getCraftableWeapons();
    const materialFiltered = craftableWeapons.filter(weapon => weapon.material === selectedMaterial);
    if (selectedWeaponCategory === 'all') return materialFiltered;
    return materialFiltered.filter(weapon => weapon.type === selectedWeaponCategory);
  };

  const craftArmorPiece = (armorType: 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
    craftArmor(armorType, material);
  };

  const craftWeaponPiece = (weaponType: 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
    craftWeapon(weaponType, material);
  };

  // Funktionen für Massen-Mining
  const startMiningForAllCompanions = (oreType: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
    const availableCompanions = companions.discovered.filter(companion => !isCompanionMining(companion.id));
    
    // Sende alle verfügbaren Begleiter
    availableCompanions.forEach(companion => {
      if (isOreTypeAvailable(oreType)) {
        startMiningForCompanion(companion.id, oreType);
      }
    });
  };

  const removeAllCompanionsFromMining = () => {
    const miningCompanions = companions.discovered.filter(companion => isCompanionMining(companion.id));
    miningCompanions.forEach(companion => {
      removeFromMining(companion.id);
    });
  };

  return (
    <div className="space-y-6">
      {/* Erz-Ressourcen Anzeige */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 text-white border border-gray-600"
      >
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
          🪨 Erz-Ressourcen
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(oreCounts).map(([oreType, count]) => {
            const oreRequirements = {
              iron: 0,
              steel: 10000,
              mithril: 50000,
              adamantium: 100000,
            };
            
            const requirement = oreRequirements[oreType as keyof typeof oreRequirements];
            const hasEnough = count >= requirement;
            const nextOreType = oreType === 'iron' ? 'steel' : oreType === 'steel' ? 'mithril' : oreType === 'mithril' ? 'adamantium' : null;
            
            return (
              <div key={oreType} className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg p-3 text-center border border-gray-500">
                <div className="text-2xl mb-1">{getOreIcon(oreType)}</div>
                <div className="text-sm font-medium capitalize text-gray-300">
                  {oreType === 'iron' ? 'Eisen' : oreType === 'steel' ? 'Stahl' : oreType === 'mithril' ? 'Mithril' : 'Adamantium'}
                </div>
                <div className="text-lg font-bold">{formatNumber(count)}</div>
                
                {/* Anforderungsanzeige für höhere Erzsorten */}
                {oreType === 'iron' && (
                  <div className="mt-2 text-xs">
                    <div className={`font-medium ${oreCounts.iron >= 10000 ? 'text-green-400' : 'text-red-400'}`}>
                      {oreCounts.iron >= 10000 ? '✓' : '✗'} {formatNumber(oreCounts.iron)}/{formatNumber(10000)}
                    </div>
                    <div className="text-gray-400">
                      für Stahl
                    </div>
                  </div>
                )}
                {oreType === 'steel' && (
                  <div className="mt-2 text-xs">
                    <div className={`font-medium ${oreCounts.steel >= 50000 ? 'text-green-400' : 'text-red-400'}`}>
                      {oreCounts.steel >= 50000 ? '✓' : '✗'} {formatNumber(oreCounts.steel)}/{formatNumber(50000)}
                    </div>
                    <div className="text-gray-400">
                      für Mithril
                    </div>
                  </div>
                )}
                {oreType === 'mithril' && (
                  <div className="mt-2 text-xs">
                    <div className={`font-medium ${oreCounts.mithril >= 100000 ? 'text-green-400' : 'text-red-400'}`}>
                      {oreCounts.mithril >= 100000 ? '✓' : '✗'} {formatNumber(oreCounts.mithril)}/{formatNumber(100000)}
                    </div>
                    <div className="text-gray-400">
                      für Adamantium
                    </div>
                  </div>
                )}
                
                {/* Progress-Bar für Anforderungen */}
                {oreType === 'iron' && (
                  <div className="mt-1 w-full bg-gray-600 rounded-full h-1">
                    <div 
                      className={`h-1 rounded-full transition-all duration-300 ${
                        oreCounts.iron >= 10000 ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min((oreCounts.iron / 10000) * 100, 100)}%` }}
                    />
                  </div>
                )}
                {oreType === 'steel' && (
                  <div className="mt-1 w-full bg-gray-600 rounded-full h-1">
                    <div 
                      className={`h-1 rounded-full transition-all duration-300 ${
                        oreCounts.steel >= 50000 ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min((oreCounts.steel / 50000) * 100, 100)}%` }}
                    />
                  </div>
                )}
                {oreType === 'mithril' && (
                  <div className="mt-1 w-full bg-gray-600 rounded-full h-1">
                    <div 
                      className={`h-1 rounded-full transition-all duration-300 ${
                        oreCounts.mithril >= 100000 ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min((oreCounts.mithril / 100000) * 100, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Tab-Navigation */}
      <div className="flex space-x-2">
        {[
          { key: 'mining', icon: '⛏️' },
          { key: 'crafting', icon: '🛡️' },
          { key: 'weapons', icon: '⚔️' },
        ].map((tab) => (
          <motion.button
            key={tab.key}
            onClick={() => setSelectedTab(tab.key as any)}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
              selectedTab === tab.key
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="text-lg">{tab.icon}</span>
          </motion.button>
        ))}
      </div>

      {/* Tab-Inhalte */}
      <AnimatePresence mode="wait" key="forge-tab-content">
        {selectedTab === 'mining' && (
          <motion.div
            key="mining"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-4"
          >
            <h4 className="text-xl font-bold text-white mb-4">Begleiter zum Erz-Abbau schicken</h4>
            
            {/* Kompakte Schnell-Aktionen */}
            <div className="bg-gradient-to-br from-blue-800 to-blue-900 rounded-xl p-4 border border-blue-600 mb-6">
              <h5 className="text-lg font-bold text-white mb-3">⚡ Schnell-Aktionen</h5>
              
              {/* Aktuelle Erz-Produktion */}
              {companions.discovered.filter(companion => isCompanionMining(companion.id)).length > 0 && (
                <div className="mb-3 p-2 bg-blue-700 rounded-lg">
                  <div className="text-xs text-blue-200 mb-1">Aktuelle Erz-Produktion:</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-xs">
                    {(['iron', 'steel', 'mithril', 'adamantium'] as const).map((oreType) => {
                      const activeMinersForOre = companions.discovered.filter(companion => 
                        isCompanionMining(companion.id) && 
                        companions.forge?.mining?.activeMiners[companion.id]?.oreType === oreType
                      );
                      
                      const totalOrePerSecond = activeMinersForOre.reduce((total, companion) => {
                        return total + calculateOrePerSecond(companion.id);
                      }, 0);
                      
                      if (totalOrePerSecond === 0) return null;
                      
                      return (
                        <div key={oreType} className="flex items-center gap-1">
                          <span>{getOreIcon(oreType)}</span>
                          <span className="text-green-400">+{formatNumber(totalOrePerSecond)}/s</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Kompakte Mining-Buttons */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                {(['iron', 'steel', 'mithril', 'adamantium'] as const).map((oreType) => {
                  const isAvailable = isOreTypeAvailable(oreType);
                  const availableCompanions = companions.discovered.filter(companion => !isCompanionMining(companion.id));
                  const hasAvailableCompanions = availableCompanions.length > 0;
                  
                  return (
                    <motion.button
                      key={oreType}
                      onClick={() => startMiningForAllCompanions(oreType)}
                      className={`p-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                        isAvailable && hasAvailableCompanions
                          ? `bg-gradient-to-r ${getOreColor(oreType)} text-white hover:scale-105`
                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                      whileHover={isAvailable && hasAvailableCompanions ? { scale: 1.05 } : {}}
                      whileTap={isAvailable && hasAvailableCompanions ? { scale: 0.95 } : {}}
                      disabled={!isAvailable || !hasAvailableCompanions}
                    >
                      <div className="text-sm mb-1">{getOreIcon(oreType)}</div>
                      <div className="text-[8px] capitalize">
                        {oreType === 'iron' ? 'Eisen' : oreType === 'steel' ? 'Stahl' : oreType === 'mithril' ? 'Mithril' : 'Adamantium'}
                      </div>
                      <div className="text-[6px] text-gray-300">
                        {availableCompanions.length} Begleiter
                      </div>
                    </motion.button>
                  );
                })}
              </div>
              
              {/* Zurückholen Button */}
              <motion.button
                onClick={removeAllCompanionsFromMining}
                className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  companions.discovered.filter(companion => isCompanionMining(companion.id)).length > 0
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={companions.discovered.filter(companion => isCompanionMining(companion.id)).length > 0 ? { scale: 1.02 } : {}}
                whileTap={companions.discovered.filter(companion => isCompanionMining(companion.id)).length > 0 ? { scale: 0.98 } : {}}
                disabled={companions.discovered.filter(companion => isCompanionMining(companion.id)).length === 0}
              >
                🚪 Alle zurückholen ({companions.discovered.filter(companion => isCompanionMining(companion.id)).length} aktiv)
              </motion.button>
            </div>
            
            {/* Abbaulaufende Begleiter - Zusammenklappbar */}
            {companions.discovered.filter(companion => isCompanionMining(companion.id)).length > 0 && (
              <div className="mb-6">
                <motion.button
                  onClick={() => setShowMiningCompanions(!showMiningCompanions)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg p-3 font-medium transition-all duration-200 flex items-center justify-between"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="flex items-center gap-2">
                    <span>⛏️</span>
                    <span>Aktive Minenarbeiter ({companions.discovered.filter(companion => isCompanionMining(companion.id)).length})</span>
                  </div>
                  <motion.div
                    animate={{ rotate: showMiningCompanions ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    ▼
                  </motion.div>
                </motion.button>

                <AnimatePresence key="mining-companions">
                  {showMiningCompanions && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {companions.discovered.filter(companion => isCompanionMining(companion.id)).map((companion) => (
                          <motion.div
                            key={companion.id}
                            className="bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl p-4 border border-blue-500"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className="text-2xl">
                                {companion.icon.startsWith('data:') ? (
                                  <img 
                                    src={companion.icon} 
                                    alt={companion.name}
                                    className="w-8 h-8 object-contain"
                                    style={{ display: 'inline-block' }}
                                  />
                                ) : (
                                  companion.icon
                                )}
                              </div>
                              <div>
                                <h5 className="font-bold text-white">{companion.name}</h5>
                                <p className="text-sm text-blue-300">Level {companion.level || 1}</p>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="text-sm text-blue-300">⛏️ Abbau läuft...</div>
                              <div className="w-full bg-gray-600 rounded-full h-2">
                                <motion.div
                                  className="bg-blue-500 h-2 rounded-full"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${getMiningProgress(companion.id)}%` }}
                                  transition={{ duration: 0.5 }}
                                />
                              </div>
                              <div className="text-xs text-gray-400">
                                {getMiningProgress(companion.id).toFixed(1)}% fertig
                              </div>
                              <div className="text-xs text-green-400">
                                +{formatNumber(calculateOrePerSecond(companion.id))} {(companions.forge?.mining?.activeMiners[companion.id]?.oreType === 'iron' ? 'Eisen' : companions.forge?.mining?.activeMiners[companion.id]?.oreType === 'steel' ? 'Stahl' : companions.forge?.mining?.activeMiners[companion.id]?.oreType === 'mithril' ? 'Mithril' : 'Adamantium')}/s
                              </div>
                              <div className="text-xs text-blue-400">
                                +{formatNumber(calculateXpPerSecond(companion.id))} XP/s
                              </div>
                              <motion.button
                                onClick={() => removeFromMining(companion.id)}
                                className="w-full mt-2 py-1 px-2 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                ⛏️ Entfernen
                              </motion.button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Verfügbare Begleiter */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {companions.discovered.filter(companion => !isCompanionMining(companion.id)).map((companion) => (
                <motion.div
                  key={companion.id}
                  className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-4 border border-gray-600"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-2xl">
                      {companion.icon.startsWith('data:') ? (
                        <img 
                          src={companion.icon} 
                          alt={companion.name}
                          className="w-8 h-8 object-contain"
                          style={{ display: 'inline-block' }}
                        />
                      ) : (
                        companion.icon
                      )}
                    </div>
                    <div>
                      <h5 className="font-bold text-white">{companion.name}</h5>
                      <p className="text-sm text-gray-400">Level {companion.level || 1}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {(['iron', 'steel', 'mithril', 'adamantium'] as const).map((oreType) => {
                      const isAvailable = isOreTypeAvailable(oreType);
                      const requirement = getOreRequirement(oreType);
                      
                      return (
                        <motion.button
                          key={oreType}
                          onClick={() => startMiningForCompanion(companion.id, oreType)}
                          className={`p-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isAvailable 
                              ? `bg-gradient-to-r ${getOreColor(oreType)} text-white hover:scale-105`
                              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          }`}
                          whileHover={isAvailable ? { scale: 1.05 } : {}}
                          whileTap={isAvailable ? { scale: 0.95 } : {}}
                          disabled={!isAvailable}
                        >
                          <div className="text-lg mb-1">{getOreIcon(oreType)}</div>
                          <div className="text-xs capitalize">
                            {oreType === 'iron' ? 'Eisen' : oreType === 'steel' ? 'Stahl' : oreType === 'mithril' ? 'Mithril' : 'Adamantium'}
                          </div>
                                                      {!isAvailable && requirement && (
                              <div className="text-[8px] text-gray-400 mt-1">
                                Braucht {requirement.amount} {requirement.type === 'iron' ? 'Eisen' : requirement.type === 'steel' ? 'Stahl' : 'Mithril'}
                              </div>
                            )}
                            {isAvailable && (
                              <div className="text-[8px] text-green-400 mt-1">
                                +{formatNumber(calculateOrePerSecond(companion.id, oreType))} {oreType === 'iron' ? 'Eisen' : oreType === 'steel' ? 'Stahl' : oreType === 'mithril' ? 'Mithril' : 'Adamantium'}/s
                              </div>
                            )}
                            {isAvailable && (
                              <div className="text-[8px] text-blue-400 mt-1">
                                +{formatNumber(calculateXpPerSecond(companion.id, oreType))} XP/s
                              </div>
                            )}
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {selectedTab === 'crafting' && (
          <motion.div
            key="crafting"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-4"
          >
            <h4 className="text-xl font-bold text-white mb-4">Rüstungen schmieden</h4>
            
            {/* Zusammenklappbare aktive Schmiede-Projekte */}
            {getActiveCrafting().length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-orange-800 to-red-800 rounded-xl border border-orange-600"
              >
                <motion.button
                  onClick={() => setShowActiveCrafting(!showActiveCrafting)}
                  className="w-full p-4 flex items-center justify-between text-white font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔨</span>
                    <span>Aktive Schmiede-Projekte ({getActiveCrafting().length})</span>
                  </div>
                  <motion.div
                    animate={{ rotate: showActiveCrafting ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-xl"
                  >
                    ▼
                  </motion.div>
                </motion.button>
                
                <AnimatePresence key="active-crafting">
                  {showActiveCrafting && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="p-4 border-t border-orange-600"
                    >
                      <div className="space-y-3">
                        {getActiveCrafting().map((armorGroup) => {
                          const groupKey = `${armorGroup.type}_${armorGroup.material}`;
                          const isExpanded = expandedArmorGroup === groupKey;
                          
                          return (
                            <motion.div
                              key={groupKey}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="bg-gradient-to-br from-orange-700 to-red-700 rounded-lg p-3 border border-orange-500"
                            >
                              <motion.button
                                onClick={() => setExpandedArmorGroup(isExpanded ? null : groupKey)}
                                className="w-full"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{getArmorIcon(armorGroup.type)}</span>
                                    <div>
                                      <span className="font-medium text-white">{armorGroup.name}</span>
                                      {armorGroup.count > 1 && (
                                        <span className="ml-2 text-sm text-orange-200 bg-orange-600 px-2 py-1 rounded-full">
                                          x{armorGroup.count}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-orange-200">
                                      {armorGroup.averageProgress.toFixed(1)}%
                                    </span>
                                    <motion.div
                                      animate={{ rotate: isExpanded ? 180 : 0 }}
                                      transition={{ duration: 0.3 }}
                                      className="text-orange-200"
                                    >
                                      ▼
                                    </motion.div>
                                  </div>
                                </div>
                                
                                <div className="w-full bg-gray-600 rounded-full h-2">
                                  <motion.div
                                    className="bg-orange-500 h-2 rounded-full"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${armorGroup.averageProgress}%` }}
                                    transition={{ duration: 0.5 }}
                                  />
                                </div>
                              </motion.button>
                              
                              {/* Einzelne Projekte */}
                              <AnimatePresence key="expanded-armor-group">
                                {isExpanded && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-3 space-y-2"
                                  >
                                    {armorGroup.items.map((armor: any, index: number) => (
                                      <motion.div
                                        key={armor.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="bg-gradient-to-br from-orange-600 to-red-600 rounded-lg p-2 border border-orange-400"
                                      >
                                        <div className="flex items-center justify-between mb-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm">{getArmorIcon(armor.type)}</span>
                                            <span className="text-sm text-white">
                                              {armor.name} #{index + 1}
                                            </span>
                                          </div>
                                          <span className="text-xs text-orange-200">
                                            {getArmorCraftingProgress(armor.id).toFixed(1)}%
                                          </span>
                                        </div>
                                        
                                        <div className="w-full bg-gray-600 rounded-full h-1">
                                          <motion.div
                                            className="bg-orange-400 h-1 rounded-full"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${getArmorCraftingProgress(armor.id)}%` }}
                                            transition={{ duration: 0.5 }}
                                          />
                                        </div>
                                      </motion.div>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
            
            {/* Material-Kategorie-Auswahl */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-600"
            >
              <h5 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span className="text-xl">🪨</span>
                Material-Auswahl
              </h5>
              <div className="grid grid-cols-2 sm:flex sm:space-x-2 gap-2 sm:gap-0 mb-4">
                {[
                  { key: 'iron', label: 'Eisen', icon: '🪨', color: 'from-gray-500 to-gray-600' },
                  { key: 'steel', label: 'Stahl', icon: '⚔️', color: 'from-blue-500 to-blue-600' },
                  { key: 'mithril', label: 'Mithril', icon: '💎', color: 'from-purple-500 to-purple-600' },
                  { key: 'adamantium', label: 'Adamantium', icon: '🔥', color: 'from-red-500 to-red-600' },
                ].map((material) => (
                  <motion.button
                    key={material.key}
                    onClick={() => setSelectedMaterial(material.key as any)}
                    className={`py-2 px-2 sm:px-3 rounded-lg font-medium transition-all duration-200 ${
                      selectedMaterial === material.key
                        ? `bg-gradient-to-r ${material.color} text-white shadow-lg`
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-center gap-1 sm:gap-2">
                      <span className="text-base sm:text-lg">{material.icon}</span>
                      <span className="text-xs sm:text-sm">{material.label}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Rüstungstyp-Kategorie-Auswahl */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-blue-800 to-indigo-900 rounded-xl p-4 border border-blue-600"
            >
              <h5 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span className="text-xl">🛡️</span>
                Rüstungstyp-Auswahl
              </h5>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                {[
                  { key: 'all', label: 'Alle', icon: '🛡️', color: 'from-blue-500 to-indigo-500' },
                  { key: 'helmet', label: 'Helm', icon: '⛑️', color: 'from-yellow-500 to-orange-500' },
                  { key: 'chestplate', label: 'Brustplatte', icon: '🛡️', color: 'from-red-500 to-pink-500' },
                  { key: 'greaves', label: 'Beinschienen', icon: '🦵', color: 'from-green-500 to-emerald-500' },
                  { key: 'boots', label: 'Stiefel', icon: '👢', color: 'from-purple-500 to-violet-500' },
                  { key: 'shield', label: 'Schild', icon: '🛡️', color: 'from-cyan-500 to-blue-500' },
                ].map((category) => (
                  <motion.button
                    key={category.key}
                    onClick={() => setSelectedArmorCategory(category.key as any)}
                    className={`py-2 px-1 sm:px-2 rounded-lg font-medium transition-all duration-200 ${
                      selectedArmorCategory === category.key
                        ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-base sm:text-lg">{category.icon}</span>
                      <span className="text-xs text-center leading-tight">{category.label}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {getFilteredArmor().map((armor, index) => (
                <motion.div
                  key={`${armor.type}_${armor.material}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-4 border border-gray-600"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{getArmorIcon(armor.type)}</span>
                    <div>
                      <h5 className="font-bold text-white">{armor.name}</h5>
                      <p className="text-sm text-gray-400 capitalize">{armor.material}</p>
                    </div>
                  </div>

                  <div className="space-y-2 mb-3">
                    {(() => {
                      const adjustedStats = getAdjustedArmorStats(armor);
                      const nextLevel = getArmorLevel(armor.type, armor.material);
                      return (
                        <>
                          <div className="text-sm text-gray-300">
                            <span className="font-bold">Verteidigung:</span> 
                            <span className="text-green-400"> +{adjustedStats.defense}</span>
                            {nextLevel > 1 && (
                              <span className="text-xs text-gray-500 ml-1">
                                (Level {nextLevel})
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-300">
                            <span className="font-bold">Haltbarkeit:</span> 
                            <span className="text-green-400"> {adjustedStats.durability}</span>
                            {nextLevel > 1 && (
                              <span className="text-xs text-gray-500 ml-1">
                                (Level {nextLevel})
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-300">
                            <span className="font-bold">Schmiedezeit:</span> {armor.craftingTime} Min
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs text-gray-400 font-bold">
                      Kosten (Level {getArmorLevel(armor.type, armor.material)}):
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {Object.entries(getAdjustedArmorCost(armor)).map(([oreType, cost]) => (
                        cost > 0 && (
                          <div key={oreType} className="flex items-center gap-1">
                            <span>{getOreIcon(oreType)}</span>
                            <span className="text-gray-300">{formatNumber(cost)}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>

                  <motion.button
                    onClick={() => craftArmorPiece(armor.type, armor.material)}
                    className={`w-full mt-3 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                      canAffordArmor(armor) 
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600' 
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                    whileHover={canAffordArmor(armor) ? { scale: 1.05 } : {}}
                    whileTap={canAffordArmor(armor) ? { scale: 0.95 } : {}}
                    disabled={!canAffordArmor(armor)}
                  >
                    {canAffordArmor(armor) ? '🔨 Schmieden' : getArmorButtonText(armor)}
                  </motion.button>
                  
                  {/* Reset-Button für Rüstungen */}
                  {getArmorLevel(armor.type, armor.material) >= 30 && (
                    <motion.button
                      onClick={() => resetArmorLevel(armor.type, armor.material)}
                      className="w-full mt-2 py-1 px-3 rounded-lg text-xs font-medium bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 transition-all duration-200"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      🔄 Zurücksetzen (Level {getArmorLevel(armor.type, armor.material)})
                    </motion.button>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {selectedTab === 'weapons' && (
          <motion.div
            key="weapons"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-4"
          >
            <h4 className="text-xl font-bold text-white mb-4">Waffen schmieden</h4>
            
            {/* Zusammenklappbare aktive Waffen-Schmiede-Projekte */}
            {getActiveWeaponCrafting().length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-purple-800 to-blue-800 rounded-xl border border-purple-600"
              >
                <motion.button
                  onClick={() => setShowActiveCrafting(!showActiveCrafting)}
                  className="w-full p-4 flex items-center justify-between text-white font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⚔️</span>
                    <span>Aktive Waffen-Projekte ({getActiveWeaponCrafting().length})</span>
                  </div>
                  <motion.div
                    animate={{ rotate: showActiveCrafting ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-xl"
                  >
                    ▼
                  </motion.div>
                </motion.button>
                
                <AnimatePresence key="active-weapon-crafting">
                  {showActiveCrafting && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="p-4 border-t border-purple-600"
                    >
                      <div className="space-y-3">
                        {getActiveWeaponCrafting().map((weaponGroup) => {
                          const groupKey = `${weaponGroup.type}_${weaponGroup.material}`;
                          const isExpanded = expandedWeaponGroup === groupKey;
                          
                          return (
                            <motion.div
                              key={groupKey}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="bg-gradient-to-br from-purple-700 to-blue-700 rounded-lg p-3 border border-purple-500"
                            >
                              <motion.button
                                onClick={() => setExpandedWeaponGroup(isExpanded ? null : groupKey)}
                                className="w-full"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{getWeaponIcon(weaponGroup.type)}</span>
                                    <div>
                                      <span className="font-medium text-white">{weaponGroup.name}</span>
                                      {weaponGroup.count > 1 && (
                                        <span className="ml-2 text-sm text-purple-200 bg-purple-600 px-2 py-1 rounded-full">
                                          x{weaponGroup.count}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-purple-200">
                                      {weaponGroup.averageProgress.toFixed(1)}%
                                    </span>
                                    <motion.div
                                      animate={{ rotate: isExpanded ? 180 : 0 }}
                                      transition={{ duration: 0.3 }}
                                      className="text-purple-200"
                                    >
                                      ▼
                                    </motion.div>
                                  </div>
                                </div>
                                
                                <div className="w-full bg-gray-600 rounded-full h-2">
                                  <motion.div
                                    className="bg-purple-500 h-2 rounded-full"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${weaponGroup.averageProgress}%` }}
                                    transition={{ duration: 0.5 }}
                                  />
                                </div>
                              </motion.button>
                              
                              {/* Einzelne Projekte */}
                              <AnimatePresence key="expanded-weapon-group">
                                {isExpanded && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-3 space-y-2"
                                  >
                                    {weaponGroup.items.map((weapon: any, index: number) => (
                                      <motion.div
                                        key={weapon.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg p-2 border border-purple-400"
                                      >
                                        <div className="flex items-center justify-between mb-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm">{getWeaponIcon(weapon.type)}</span>
                                            <span className="text-sm text-white">
                                              {weapon.name} #{index + 1}
                                            </span>
                                          </div>
                                          <span className="text-xs text-purple-200">
                                            {getWeaponCraftingProgress(weapon.id).toFixed(1)}%
                                          </span>
                                        </div>
                                        
                                        <div className="w-full bg-gray-600 rounded-full h-1">
                                          <motion.div
                                            className="bg-purple-400 h-1 rounded-full"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${getWeaponCraftingProgress(weapon.id)}%` }}
                                            transition={{ duration: 0.5 }}
                                          />
                                        </div>
                                      </motion.div>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
            
            {/* Material-Kategorie-Auswahl */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-600"
            >
              <h5 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span className="text-xl">🪨</span>
                Material-Auswahl
              </h5>
              <div className="grid grid-cols-2 sm:flex sm:space-x-2 gap-2 sm:gap-0 mb-4">
                {[
                  { key: 'iron', label: 'Eisen', icon: '🪨', color: 'from-gray-500 to-gray-600' },
                  { key: 'steel', label: 'Stahl', icon: '⚔️', color: 'from-blue-500 to-blue-600' },
                  { key: 'mithril', label: 'Mithril', icon: '💎', color: 'from-purple-500 to-purple-600' },
                  { key: 'adamantium', label: 'Adamantium', icon: '🔥', color: 'from-red-500 to-red-600' },
                ].map((material) => (
                  <motion.button
                    key={material.key}
                    onClick={() => setSelectedMaterial(material.key as any)}
                    className={`py-2 px-2 sm:px-3 rounded-lg font-medium transition-all duration-200 ${
                      selectedMaterial === material.key
                        ? `bg-gradient-to-r ${material.color} text-white shadow-lg`
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-center gap-1 sm:gap-2">
                      <span className="text-base sm:text-lg">{material.icon}</span>
                      <span className="text-xs sm:text-sm">{material.label}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Waffentyp-Kategorie-Auswahl */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-xl p-4 border border-purple-600"
            >
              <h5 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span className="text-xl">⚔️</span>
                Waffentyp-Auswahl
              </h5>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {[
                  { key: 'all', label: 'Alle', icon: '⚔️', color: 'from-purple-500 to-indigo-500' },
                  { key: 'sword', label: 'Schwert', icon: '⚔️', color: 'from-blue-500 to-cyan-500' },
                  { key: 'axe', label: 'Axt', icon: '🪓', color: 'from-red-500 to-orange-500' },
                  { key: 'mace', label: 'Streitkolben', icon: '🔨', color: 'from-yellow-500 to-amber-500' },
                  { key: 'dagger', label: 'Dolch', icon: '🗡️', color: 'from-gray-500 to-slate-500' },
                  { key: 'bow', label: 'Bogen', icon: '🏹', color: 'from-green-500 to-emerald-500' },
                  { key: 'staff', label: 'Stab', icon: '🦯', color: 'from-purple-500 to-violet-500' },
                ].map((category) => (
                  <motion.button
                    key={category.key}
                    onClick={() => setSelectedWeaponCategory(category.key as any)}
                    className={`py-2 px-1 sm:px-2 rounded-lg font-medium transition-all duration-200 ${
                      selectedWeaponCategory === category.key
                        ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-base sm:text-lg">{category.icon}</span>
                      <span className="text-xs text-center leading-tight">{category.label}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {getFilteredWeapons().map((weapon, index) => (
                <motion.div
                  key={`${weapon.type}_${weapon.material}`}
                  className="bg-gradient-to-br from-purple-700 to-blue-800 rounded-xl p-4 border border-purple-500"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl">{getWeaponIcon(weapon.type)}</span>
                    <div>
                      <h5 className="font-bold text-white">{weapon.name}</h5>
                      <p className="text-sm text-purple-300 capitalize">{weapon.material}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {(() => {
                      const adjustedStats = getAdjustedWeaponStats(weapon);
                      const nextLevel = getWeaponLevel(weapon.type, weapon.material);
                      return (
                        <>
                          <div className="text-purple-300">
                            <span className="font-bold">Angriff:</span> 
                            <span className="text-green-400"> +{adjustedStats.attack}</span>
                            {nextLevel > 1 && (
                              <span className="text-xs text-gray-500 ml-1">
                                (Level {nextLevel})
                              </span>
                            )}
                          </div>
                          <div className="text-purple-300">
                            <span className="font-bold">Haltbarkeit:</span> 
                            <span className="text-green-400"> {adjustedStats.durability}</span>
                            {nextLevel > 1 && (
                              <span className="text-xs text-gray-500 ml-1">
                                (Level {nextLevel})
                              </span>
                            )}
                          </div>
                          <div className="text-purple-300">
                            <span className="font-bold">Schmiede-Zeit:</span> {weapon.craftingTime} Min.
                          </div>
                        </>
                      );
                    })()}
                    
                    <div className="text-xs text-gray-400 space-y-1">
                      <div>Kosten (Level {getWeaponLevel(weapon.type, weapon.material)}):</div>
                      {(() => {
                        const adjustedCost = getAdjustedWeaponCost(weapon);
                        return (
                          <>
                            {adjustedCost.iron > 0 && <div>🪨 Eisen: {formatNumber(adjustedCost.iron)}</div>}
                            {adjustedCost.steel > 0 && <div>⚔️ Stahl: {formatNumber(adjustedCost.steel)}</div>}
                            {adjustedCost.mithril > 0 && <div>💎 Mithril: {formatNumber(adjustedCost.mithril)}</div>}
                            {adjustedCost.adamantium > 0 && <div>🔥 Adamantium: {formatNumber(adjustedCost.adamantium)}</div>}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <motion.button
                    onClick={() => craftWeaponPiece(weapon.type, weapon.material)}
                    className={`w-full mt-3 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                      canAffordWeapon(weapon)
                        ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600' 
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                    whileHover={canAffordWeapon(weapon) ? { scale: 1.05 } : {}}
                    whileTap={canAffordWeapon(weapon) ? { scale: 0.95 } : {}}
                    disabled={!canAffordWeapon(weapon)}
                  >
                    {canAffordWeapon(weapon) ? '⚔️ Schmieden' : getWeaponButtonText(weapon)}
                  </motion.button>
                  
                  {/* Reset-Button für Waffen */}
                  {getWeaponLevel(weapon.type, weapon.material) >= 30 && (
                    <motion.button
                      onClick={() => resetWeaponLevel(weapon.type, weapon.material)}
                      className="w-full mt-2 py-1 px-3 rounded-lg text-xs font-medium bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 transition-all duration-200"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      🔄 Zurücksetzen (Level {getWeaponLevel(weapon.type, weapon.material)})
                    </motion.button>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}


      </AnimatePresence>
    </div>
  );
};

export default CompanionForge; 